import React from 'react';

export function selectedCanvasNode(editor) {
  const { selection } = editor.state;
  if (selection.node?.type.name === 'image')
    return { node: selection.node, pos: selection.from };
  for (let depth = selection.$from.depth; depth > 0; depth--) {
    const node = selection.$from.node(depth);
    if (node.type.name === 'layout')
      return { node, pos: selection.$from.before(depth) };
  }
  return null;
}

export function updateCanvasNode(editor, target, attrs) {
  let position = null;
  editor.state.doc.descendants((node, pos) => {
    if (node === target) position = pos;
  });
  if (position === null) return false;
  editor.view.dispatch(
    editor.state.tr.setNodeMarkup(position, undefined, {
      ...target.attrs,
      ...attrs,
    })
  );
  return true;
}

export default function CanvasInspector({ editor, onUploadImage, onBusy }) {
  const selected = selectedCanvasNode(editor);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');
  const mounted = React.useRef(true);
  React.useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  if (!selected) return null;
  const { node, pos } = selected;
  const isImage = node.type.name === 'image';
  const changeStyle = (property, value) => {
    const el = document.createElement('div');
    el.setAttribute('style', node.attrs.style || '');
    el.style.setProperty(property, value);
    updateCanvasNode(editor, node, { style: el.getAttribute('style') });
  };
  const currentStyle = document.createElement('div');
  currentStyle.setAttribute('style', node.attrs.style || '');
  return (
    <div
      className="lt-element-inspector"
      role="group"
      aria-label={isImage ? 'Image settings' : 'Section settings'}
    >
      <strong>{isImage ? 'Image' : node.attrs.label || 'Section'}</strong>
      {isImage ? (
        <>
          <label className="lt-image-replace">
            {uploading ? 'Uploading…' : 'Replace image'}
            <input
              aria-label="Replace image file"
              type="file"
              accept="image/*"
              hidden
              disabled={uploading || !onUploadImage}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                if (!file.type.startsWith('image/')) {
                  setError('Choose an image file.');
                  return;
                }
                const target = node;
                setUploading(true);
                setError('');
                onBusy?.(true);
                try {
                  const src = await onUploadImage(file);
                  if (
                    mounted.current &&
                    !editor.isDestroyed &&
                    !updateCanvasNode(editor, target, { src, alt: file.name })
                  )
                    setError(
                      'That image changed while uploading. Select it and try again.'
                    );
                } catch (_) {
                  if (mounted.current)
                    setError('The image couldn’t upload. Please try again.');
                } finally {
                  if (mounted.current) setUploading(false);
                  onBusy?.(false);
                }
              }}
            />
          </label>
          <input
            aria-label="Replacement image URL"
            placeholder="Or paste an image URL"
            value={imageUrl}
            disabled={uploading}
            onChange={(event) => setImageUrl(event.target.value)}
          />
          <button
            type="button"
            disabled={uploading || !/^https?:\/\//i.test(imageUrl.trim())}
            onClick={() => {
              updateCanvasNode(editor, node, { src: imageUrl.trim() });
              setImageUrl('');
            }}
          >
            Use image
          </button>
          <label>
            Image description{' '}
            <input
              aria-label="Image description"
              value={node.attrs.alt || ''}
              disabled={uploading}
              onChange={(event) =>
                updateCanvasNode(editor, node, { alt: event.target.value })
              }
            />
          </label>
          <label>
            Width{' '}
            <select
              aria-label="Image width"
              disabled={uploading}
              value={currentStyle.style.width || '100%'}
              onChange={(event) => changeStyle('width', event.target.value)}
            >
              {['25%', '50%', '75%', '100%'].map((size) => (
                <option key={size}>{size}</option>
              ))}
            </select>
          </label>
        </>
      ) : (
        <>
          <label>
            Background{' '}
            <input
              type="color"
              aria-label="Section background"
              defaultValue="#faf7ef"
              onChange={(event) =>
                changeStyle('background', event.target.value)
              }
            />
          </label>
          <label>
            Text{' '}
            <input
              type="color"
              aria-label="Section text color"
              defaultValue="#304531"
              onChange={(event) => changeStyle('color', event.target.value)}
            />
          </label>
          <label>
            Spacing{' '}
            <select
              aria-label="Section spacing"
              value={currentStyle.style.padding || '0px'}
              onChange={(event) => changeStyle('padding', event.target.value)}
            >
              {['0px', '16px', '24px', '32px', '48px'].map((size) => (
                <option key={size} value={size}>
                  {size.replace('px', '')}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertContentAt(pos + node.nodeSize, node.toJSON())
                .run()
            }
          >
            Duplicate section
          </button>
        </>
      )}
      <button
        type="button"
        disabled={uploading}
        onClick={() =>
          editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + node.nodeSize })
            .run()
        }
      >
        Remove {isImage ? 'image' : 'section'}
      </button>
      {error && <span role="alert">{error}</span>}
    </div>
  );
}
